/**
 * TanStack Query hooks для админки (Phase 1).
 *
 * Что внутри:
 * - useAdminSummary — сводка для дашборда
 * - useProviders / useCreateProvider / useUpdateProvider / useDeleteProvider — CRUD ключей
 * - useModules — read-only 17 модулей
 * - usePresets / useUpdatePreset — пресеты (только PATCH margin_multiplier)
 * - useSystemPrompts — read-only промпты
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import api from '@/lib/api';

// === Types ===
export type ProviderName =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'openrouter'
  | 'perplexity'
  | 'deepl'
  | 'serpapi';

export type ProviderStatus = 'active' | 'exhausted' | 'error' | 'disabled';

export interface AIProviderConfig {
  id: string;
  provider: ProviderName;
  display_name: string;
  api_key_masked: string | null;
  status: ProviderStatus;
  exhausted_until: string | null;
  last_used_at: string | null;
  last_error: string | null;
  error_count: number;
  // Block 1: health-based rotation
  success_count: number;
  last_success_at: string | null;
  is_default: boolean;
  sort_order: number;
  pinned_model: string | null;
  // TZ 2026-09-23 Блок 5: «доступен тарифам» (пусто = все тарифы).
  allowed_tiers: string[];
}

export interface ProviderTestResult {
  success: boolean;
  latency_ms: number;
  http_status: number | null;
  error_message: string | null;
  model_used: string | null;
}

export interface AIProviderConfigCreate {
  provider: ProviderName;
  display_name: string;
  api_key: string;
  is_default?: boolean;
  pinned_model?: string | null;
  sort_order?: number;
  allowed_tiers?: string[] | null;
}

export interface AIProviderConfigUpdate {
  display_name?: string;
  api_key?: string;
  is_default?: boolean;
  pinned_model?: string | null;
  sort_order?: number;
  status?: ProviderStatus;
  allowed_tiers?: string[] | null;
}

// === TZ 2026-09-23 Блок 3: агрегаты провайдеров + каталог моделей ===
export interface ProviderSummary {
  provider: ProviderName;
  display_name: string;
  supports_model: boolean;
  total_keys: number;
  active_keys: number;
  has_active_keys: boolean;
}

export interface ProviderModel {
  id: string;
  name: string;
  is_free: boolean;
  context_length: number | null;
}

export interface ProviderModelsResponse {
  provider: ProviderName;
  supports_model: boolean;
  cached: boolean;
  error: string | null;
  items: ProviderModel[];
}

export interface PresetTier {
  id: string;
  key: 'economy' | 'optimal' | 'max' | 'super';
  display_name_ua: string;
  display_name_ru: string;
  display_name_en: string;
  margin_multiplier: string;
  is_active: boolean;
  sort_order: number;
}

export interface PresetTierUpdate {
  margin_multiplier?: number;
  is_active?: boolean;
}

export interface AIModule {
  id: string;
  key: string;
  display_name_ua: string;
  display_name_ru: string;
  display_name_en: string;
  description: string | null;
  category: 'content' | 'search' | 'media' | 'translation' | 'utility';
  is_active: boolean;
  sort_order: number;
  is_user_togglable: boolean;
}

export interface SystemPrompt {
  id: string;
  key: string;
  language: string;
  prompt_text: string;
}

export interface AdminSummary {
  providers_total: number;
  providers_active: number;
  modules_total: number;
  modules_active: number;
  presets_total: number;
  prompts_total: number;
  system_prompts_total: number;
}

// === Helpers ===
export function getModuleDisplayName(m: AIModule, locale: string): string {
  const key = (locale || 'ru').slice(0, 2);
  if (key === 'ua') return m.display_name_ua;
  if (key === 'en') return m.display_name_en;
  return m.display_name_ru;
}

export function getPresetDisplayName(p: PresetTier, locale: string): string {
  const key = (locale || 'ru').slice(0, 2);
  if (key === 'ua') return p.display_name_ua;
  if (key === 'en') return p.display_name_en;
  return p.display_name_ru;
}

// === Summary ===
export function useAdminSummary(options?: Partial<UseQueryOptions<AdminSummary>>) {
  return useQuery<AdminSummary>({
    queryKey: ['admin', 'summary'],
    queryFn: async () => (await api.get<AdminSummary>('/admin/summary')).data,
    ...options,
  });
}

// === Providers ===
export function useProviders() {
  return useQuery<AIProviderConfig[]>({
    queryKey: ['admin', 'providers'],
    queryFn: async () => {
      const r = await api.get<{ items: AIProviderConfig[]; total: number }>('/admin/providers');
      return r.data.items;
    },
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AIProviderConfigCreate) =>
      (await api.post<AIProviderConfig>('/admin/providers', body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
    },
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: AIProviderConfigUpdate }) =>
      (await api.patch<AIProviderConfig>(`/admin/providers/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
    },
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/providers/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      qc.invalidateQueries({ queryKey: ['admin', 'summary'] });
    },
  });
}

// Block 1: ping ключа — реальный API call (max_tokens=1 для LLM, минимальный
// request для DeepL/SerpAPI). 501 для Stability/Perplexity пока нет адаптера.
// Side effects в БД: error_count+=1 на не-успех, exhausted_until на 429.
export function useTestProvider() {
  const qc = useQueryClient();
  return useMutation<ProviderTestResult, Error, string>({
    mutationFn: async (id: string) =>
      (await api.post<ProviderTestResult>(`/admin/providers/${id}/test`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

// === Modules (read-only) ===
export function useModules() {
  return useQuery<AIModule[]>({
    queryKey: ['admin', 'modules'],
    queryFn: async () => {
      const r = await api.get<{ items: AIModule[]; total: number }>('/admin/modules');
      return r.data.items;
    },
    select: (modules) => [...modules].sort((a, b) => a.sort_order - b.sort_order),
  });
}

// === Presets ===
export function usePresets() {
  return useQuery<PresetTier[]>({
    queryKey: ['admin', 'presets'],
    queryFn: async () => {
      const r = await api.get<{ items: PresetTier[]; total: number }>('/admin/presets');
      return r.data.items;
    },
  });
}

export function useUpdatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PresetTierUpdate }) =>
      (await api.patch<PresetTier>(`/admin/presets/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'presets'] });
    },
  });
}

// === System prompts (read-only) ===
export function useSystemPrompts() {
  return useQuery<SystemPrompt[]>({
    queryKey: ['admin', 'system-prompts'],
    queryFn: async () => {
      const r = await api.get<{ items: SystemPrompt[]; total: number }>('/admin/system-prompts');
      return r.data.items;
    },
  });
}

// === Module Configs (Phase 3) ===
export interface ModuleConfig {
  id: string;
  tier_id: string;
  tier_key: 'economy' | 'optimal' | 'max' | 'super';
  module_id: string;
  module_key: string;
  module_display_name: string;
  provider_id: string;
  provider_name: string;
  model_name: string;
  system_prompt: string;
  temperature: string;
  top_p: string;
  frequency_penalty: string;
  max_tokens: number;
  input_price_per_1m: string;
  output_price_per_1m: string;
  is_unlocked: boolean;
  sort_order: number;
  // Block S2: admin-editable per-tier knobs (e.g. search_budget for search_enrichment).
  params: Record<string, unknown>;
}

export interface ModuleConfigUpdate {
  // Block A (search v3, 2026-09-22): выбор провайдера для (tier × module).
  provider_id?: string;
  model_name?: string;
  system_prompt?: string;
  temperature?: number;
  top_p?: number;
  frequency_penalty?: number;
  max_tokens?: number;
  input_price_per_1m?: number;
  output_price_per_1m?: number;
  is_unlocked?: boolean;
  sort_order?: number;
  // Block S2: replace (deep-merge on server) for the params JSONB.
  // Common shape: { search_budget: { max_queries, max_pages_per_row, ... } }.
  params?: Record<string, unknown>;
}

export function useModuleConfigs(tierKey: 'economy' | 'optimal' | 'max' | null) {
  return useQuery<ModuleConfig[]>({
    queryKey: ['admin', 'module-configs', tierKey],
    queryFn: async () => {
      if (!tierKey) return [];
      const r = await api.get<{ items: ModuleConfig[]; total: number }>(
        '/admin/module-configs',
        { params: { tier: tierKey } },
      );
      return r.data.items;
    },
    enabled: Boolean(tierKey),
  });
}

// Block A (search v3, 2026-09-22): все конфиги разом — для UI «карточка
// операции → тарифы внутри» (раньше было «таб тарифа → модули внутри»).
export function useAllModuleConfigs() {
  return useQuery<ModuleConfig[]>({
    queryKey: ['admin', 'module-configs', 'all'],
    queryFn: async () => {
      const r = await api.get<{ items: ModuleConfig[]; total: number }>(
        '/admin/module-configs',
      );
      return r.data.items;
    },
  });
}

export function useUpdateModuleConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ModuleConfigUpdate }) =>
      (await api.patch<ModuleConfig>(`/admin/module-configs/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'module-configs'] });
    },
  });
}

// === MasterTemplate (Block 2) ===

export type MasterTemplateScope = 'global' | 'user';
export type MasterTemplateStatus = 'uploading' | 'parsing' | 'ready' | 'error';

export interface MasterTemplate {
  id: string;
  display_name: string;
  original_filename: string;
  file_size_bytes: number;
  status: MasterTemplateStatus;
  error_message: string | null;
  taxonomy: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface MasterTemplateListResponse {
  items: MasterTemplate[];
  total: number;
  scope: MasterTemplateScope;
}

export interface MasterTemplateUploadResponse {
  id: string;
  scope: MasterTemplateScope;
  status: MasterTemplateStatus;
  original_filename: string;
  file_size_bytes: number;
}

export function useMasterTemplates(scope: MasterTemplateScope) {
  return useQuery<MasterTemplate[]>({
    queryKey: ['admin', 'master-templates', scope],
    queryFn: async () => {
      const r = await api.get<MasterTemplateListResponse>('/admin/master-templates', {
        params: { scope },
      });
      return r.data.items;
    },
    refetchInterval: (q) => {
      const data = q.state.data as MasterTemplate[] | undefined;
      if (!data) return false;
      const hasInflight = data.some(
        (mt) => mt.status === 'uploading' || mt.status === 'parsing',
      );
      return hasInflight ? 2_000 : false;
    },
  });
}

export function useMasterTemplate(id: string | null, scope: MasterTemplateScope) {
  return useQuery<MasterTemplate>({
    queryKey: ['admin', 'master-template', scope, id],
    queryFn: async () =>
      (await api.get<MasterTemplate>(`/admin/master-templates/${id}`, { params: { scope } })).data,
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const data = q.state.data as MasterTemplate | undefined;
      if (!data) return false;
      return data.status === 'uploading' || data.status === 'parsing' ? 2_000 : false;
    },
  });
}

export function useUploadMasterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      file,
      displayName,
    }: {
      scope: MasterTemplateScope;
      file: File;
      displayName?: string;
    }) => {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post<MasterTemplateUploadResponse>(
        '/admin/master-templates/upload',
        form,
        { params: { scope, ...(displayName ? { display_name: displayName } : {}) }, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return r.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'master-templates', data.scope] });
    },
  });
}

export function useReparseMasterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scope }: { id: string; scope: MasterTemplateScope }) =>
      (await api.post(`/admin/master-templates/${id}/reparse`, {}, { params: { scope } })).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'master-templates', vars.scope] });
      qc.invalidateQueries({ queryKey: ['admin', 'master-template', vars.scope, vars.id] });
    },
  });
}

export function useDeleteMasterTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, scope }: { id: string; scope: MasterTemplateScope }) => {
      await api.delete(`/admin/master-templates/${id}`, { params: { scope } });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'master-templates', vars.scope] });
    },
  });
}

export function useDownloadBlankTemplate() {
  return useMutation({
    mutationFn: async () => {
      const r = await api.get('/admin/master-templates/blank-template', {
        responseType: 'blob',
      });
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'master_template_blank.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  });
}

// === TZ 2026-09-23 Блок 3: агрегаты провайдеров + каталог моделей ===

/** Сводка по провайдерам: has_active_keys для дискейбла, supports_model для поля модели. */
export function useProviderSummaries() {
  return useQuery<ProviderSummary[]>({
    queryKey: ['admin', 'provider-summaries'],
    queryFn: async () => {
      const r = await api.get<{ items: ProviderSummary[]; total: number }>(
        '/admin/providers/summary',
      );
      return r.data.items;
    },
  });
}

/** Динамический список моделей провайдера (кэш 1ч на сервере). */
export function useProviderModels(provider: ProviderName | null) {
  return useQuery<ProviderModelsResponse>({
    queryKey: ['admin', 'provider-models', provider],
    queryFn: async () =>
      (await api.get<ProviderModelsResponse>('/admin/providers/models', {
        params: { provider },
      })).data,
    enabled: Boolean(provider),
    staleTime: 30 * 60_000,
  });
}

// === TZ 2026-09-23 Блок 5: именованные карусели ключей ===
export interface CarouselKey {
  id: string;
  display_name: string;
  provider: ProviderName;
  api_key_masked: string | null;
  status: ProviderStatus;
  allowed_tiers: string[];
}

export interface KeyCarouselSet {
  id: string;
  name: string;
  provider: ProviderName;
  is_active: boolean;
  sort_order: number;
  allowed_tiers: string[];
  key_ids: string[];
  keys: CarouselKey[];
}

export interface KeyCarouselSetCreate {
  name: string;
  provider: ProviderName;
  is_active?: boolean;
  sort_order?: number;
  allowed_tiers?: string[] | null;
  key_ids?: string[];
}

export interface KeyCarouselSetUpdate {
  name?: string;
  is_active?: boolean;
  sort_order?: number;
  allowed_tiers?: string[] | null;
  key_ids?: string[] | null;
}

export function useCarousels(options?: Partial<UseQueryOptions<KeyCarouselSet[]>>) {
  return useQuery<KeyCarouselSet[]>({
    queryKey: ['admin', 'carousels'],
    queryFn: async () => {
      const r = await api.get<{ items: KeyCarouselSet[]; total: number }>(
        '/admin/carousels',
      );
      return r.data.items;
    },
    ...options,
  });
}

export function useCreateCarousel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: KeyCarouselSetCreate) =>
      (await api.post<KeyCarouselSet>('/admin/carousels', body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'carousels'] });
    },
  });
}

export function useUpdateCarousel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: KeyCarouselSetUpdate }) =>
      (await api.patch<KeyCarouselSet>(`/admin/carousels/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'carousels'] });
    },
  });
}

export function useDeleteCarousel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/carousels/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'carousels'] });
    },
  });
}

// === TZ 2026-09-23 Блок 10: «Проверить всё» ===
export interface CheckAllItem {
  module_key: string;
  module_display_name: string;
  tier_key: string;
  config_id: string;
  provider: string;
  model: string;
  carousel: string | null;
  ok: boolean;
  http_status: number | null;
  latency_ms: number;
  error_text: string | null;
  model_used: string | null;
}

export interface CheckAllResponse {
  checked_at: string;
  cached: boolean;
  total: number;
  ok_count: number;
  fail_count: number;
  skipped_count: number;
  results: CheckAllItem[];
}

export function useCheckAll() {
  return useMutation({
    mutationFn: async () =>
      (await api.post<CheckAllResponse>('/admin/check-all', {})).data,
  });
}
